import React from "react";
import { Link } from "react-router-dom";

/* ---------------------------------------------------------------
   Buktu brand kit — primitives styled straight from the design
   tokens (Poppins/DM Sans, green-anchored, generous radii).
   --------------------------------------------------------------- */

export function Logo({ height = 30, to = "buktu", showWord = true, reverse = false, testId }) {
  const marks = {
    buktu: "/brand/buktu-mark.svg",
    vitals: "/brand/vitals-mark.svg",
    catalyst: "/brand/catalyst-mark.svg",
  };
  const word = { buktu: "buktu", vitals: "vitals", catalyst: "catalyst" }[to];
  const content = to === "buktu" ? (
    <img
      src={reverse ? "/brand/buktu-wordmark-reverse.png" : "/brand/buktu-wordmark.png"}
      alt="Buktu"
      style={{ height, width: "auto", display: "block" }}
    />
  ) : (
    <>
      <img src={marks[to]} alt="" style={{ width: height, height }} />
      {showWord && (
        <span
          style={{
            fontFamily: "var(--font-display)",
            fontWeight: 600,
            fontSize: height * 0.78,
            letterSpacing: "-0.03em",
            color: "var(--buktu-green)",
            lineHeight: 1,
          }}
        >
          {word}
        </span>
      )}
    </>
  );
  return (
    <Link
      to="/"
      data-testid={testId}
      aria-label="Buktu home"
      style={{ display: "inline-flex", alignItems: "center", gap: to === "buktu" ? 0 : "var(--space-3)", borderBottom: 0 }}
    >
      {content}
    </Link>
  );
}

export function Button({
  children,
  variant = "primary",
  size = "md",
  full = false,
  iconLeft,
  iconRight,
  style,
  ...props
}) {
  const heights = { sm: "var(--control-h-sm)", md: "var(--control-h-md)", lg: "var(--control-h-lg)" };
  const pads = { sm: "0 var(--space-4)", md: "0 var(--space-5)", lg: "0 var(--space-6)" };
  const fs = { sm: 13, md: 14, lg: 15 };
  const variants = {
    primary: { background: "var(--buktu-green)", color: "#fff", border: "1.5px solid var(--buktu-green)" },
    secondary: { background: "var(--surface-card)", color: "var(--text-strong)", border: "1.5px solid var(--border-strong)" },
    ghost: { background: "transparent", color: "var(--text-body)", border: "1.5px solid transparent" },
    accent: { background: "var(--accent-product)", color: "#fff", border: "1.5px solid var(--accent-product)" },
  };
  return (
    <button
      style={{
        height: heights[size],
        padding: pads[size],
        borderRadius: "var(--radius-pill)",
        cursor: "pointer",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        gap: "var(--space-2)",
        fontFamily: "var(--font-text)",
        fontWeight: 600,
        fontSize: fs[size],
        width: full ? "100%" : undefined,
        transition: "var(--transition-control)",
        ...variants[variant],
        ...style,
      }}
      onMouseEnter={(e) => {
        if (variant === "primary" || variant === "accent") e.currentTarget.style.filter = "brightness(1.08)";
        e.currentTarget.style.transform = "translateY(-1px)";
        if (variant === "primary") e.currentTarget.style.boxShadow = "var(--shadow-brand)";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.filter = "";
        e.currentTarget.style.transform = "";
        e.currentTarget.style.boxShadow = "";
      }}
      {...props}
    >
      {iconLeft}
      {children}
      {iconRight}
    </button>
  );
}

export function Card({ children, padding = "var(--space-6)", tone, interactive, style, ...props }) {
  const tones = {
    wash: { background: "var(--surface-brand-wash)", border: "1px solid transparent" },
    deep: { background: "var(--buktu-green-dark)", border: "1px solid rgba(255,255,255,0.14)" },
  };
  const isDeep = tone === "deep";
  return (
    <div
      data-surface={isDeep ? "deep" : undefined}
      style={{
        background: "var(--surface-card)",
        border: "1px solid var(--border-hairline)",
        borderRadius: "var(--radius-lg)",
        boxShadow: "var(--shadow-sm)",
        padding,
        transition: "var(--transition-control)",
        cursor: interactive ? "pointer" : undefined,
        ...(tone ? tones[tone] : {}),
        ...style,
      }}
      onMouseEnter={interactive ? (e) => { e.currentTarget.style.transform = "translateY(-2px)"; e.currentTarget.style.boxShadow = "var(--shadow-lg)"; } : undefined}
      onMouseLeave={interactive ? (e) => { e.currentTarget.style.transform = ""; e.currentTarget.style.boxShadow = "var(--shadow-sm)"; } : undefined}
      {...props}
    >
      {children}
    </div>
  );
}

export function Badge({ children, tone = "neutral", dot = false, ...rest }) {
  const tones = {
    neutral: { bg: "var(--ink-100)", fg: "var(--text-muted)", dot: "var(--ink-400)" },
    good: { bg: "var(--status-good-soft)", fg: "var(--buktu-green-deep)", dot: "var(--status-good)" },
    watch: { bg: "var(--status-watch-soft)", fg: "#7a5a00", dot: "var(--status-watch)" },
    risk: { bg: "var(--status-risk-soft)", fg: "#8a2a1a", dot: "var(--status-risk)" },
    accent: { bg: "var(--surface-brand-wash)", fg: "var(--buktu-green-deep)", dot: "var(--buktu-green)" },
  };
  const t = tones[tone] || tones.neutral;
  return (
    <span
      {...rest}
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        padding: "3px 10px",
        borderRadius: "var(--radius-pill)",
        background: t.bg,
        color: t.fg,
        font: "var(--type-eyebrow)",
        fontWeight: 600,
        whiteSpace: "nowrap",
      }}
    >
      {dot && <span style={{ width: 7, height: 7, borderRadius: 999, background: t.dot }} />}
      {children}
    </span>
  );
}

export function ScoreRing({ value = 0, max = 100, size = 128, thickness = 12, tone = "auto", animate = true, subLabel }) {
  const pct = Math.max(0, Math.min(1, value / max));
  const [shown, setShown] = React.useState(animate ? 0 : pct);
  React.useEffect(() => {
    if (!animate) { setShown(pct); return; }
    const t = requestAnimationFrame(() => setShown(pct));
    return () => cancelAnimationFrame(t);
  }, [pct, animate]);
  const r = (size - thickness) / 2;
  const c = 2 * Math.PI * r;
  const color =
    tone === "auto"
      ? pct >= 0.7 ? "var(--status-good)" : pct >= 0.4 ? "var(--status-watch)" : "var(--status-risk)"
      : tone === "accent" ? "var(--accent-product)" : `var(--status-${tone})`;
  return (
    <div style={{ position: "relative", width: size, height: size }}>
      <svg width={size} height={size} style={{ display: "block", transform: "rotate(-90deg)" }}>
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="var(--ink-100)" strokeWidth={thickness} />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke={color}
          strokeWidth={thickness}
          strokeLinecap="round"
          strokeDasharray={c}
          strokeDashoffset={c * (1 - shown)}
          style={{ transition: "stroke-dashoffset var(--dur-reveal) var(--ease-out)" }}
        />
      </svg>
      <div style={{ position: "absolute", inset: 0, display: "grid", placeItems: "center" }}>
        <div style={{ textAlign: "center" }}>
          <span style={{ fontFamily: "var(--font-display)", fontWeight: 600, fontSize: size * 0.3, lineHeight: 1, letterSpacing: "var(--tracking-tight)", color: "var(--text-strong)" }}>
            {Math.round(value)}
          </span>
          {subLabel && <div style={{ font: "var(--type-eyebrow)", color: "var(--text-faint)", marginTop: 2 }}>{subLabel}</div>}
        </div>
      </div>
    </div>
  );
}

export function ProgressTrack({ value = 0, max = 100, height = 8, tone = "accent" }) {
  const pct = Math.max(0, Math.min(1, value / max)) * 100;
  const color =
    tone === "accent" ? "var(--accent-product)" :
    tone === "brand" ? "var(--buktu-green)" : `var(--status-${tone})`;
  return (
    <div style={{ height, borderRadius: 999, background: "var(--ink-100)", overflow: "hidden" }}>
      <div style={{ width: pct + "%", height: "100%", borderRadius: 999, background: color, transition: "width var(--dur-slow) var(--ease-out)" }} />
    </div>
  );
}

export function Metric({ label, value, unit, note, delta, deltaTone = "good" }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
      <span className="buktu-eyebrow">{label}</span>
      <div style={{ display: "flex", alignItems: "baseline", gap: 6 }}>
        <span style={{ fontFamily: "var(--font-display)", fontWeight: 600, fontSize: 28, letterSpacing: "var(--tracking-tight)", color: "var(--text-strong)" }}>{value}</span>
        {unit && <span style={{ font: "var(--type-body-sm)", color: "var(--text-muted)" }}>{unit}</span>}
        {delta && <span style={{ font: "var(--type-data)", fontSize: 12, color: deltaTone === "risk" ? "var(--status-risk)" : "var(--status-good)" }}>{delta}</span>}
      </div>
      {note && <span style={{ font: "var(--type-body-sm)", fontSize: 12, color: "var(--text-faint)" }}>{note}</span>}
    </div>
  );
}

export function Field({ label, hint, children, htmlFor }) {
  return (
    <label htmlFor={htmlFor} style={{ display: "flex", flexDirection: "column", gap: "var(--space-2)" }}>
      {label && <span style={{ font: "var(--type-label)", color: "var(--text-strong)" }}>{label}</span>}
      {children}
      {hint && <span style={{ font: "var(--type-body-sm)", fontSize: 12, color: "var(--text-faint)" }}>{hint}</span>}
    </label>
  );
}
